import * as React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Link } from "react-router";

import { useAuth } from "@/providers";
import { TemplateField } from "@/shared/components/TemplateField";
import { useSlackOutboundChannels } from "@/shared/domain/slack/hooks";
import { Field, Select, type SelectItem } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import { FormBlock } from "../../invocationSpec/FormBlock";
import { type TriggerFormValues } from "../triggerFormSchema";
import { useFormTemplateCatalog } from "../useFormTemplateCatalog";

import styles from "./targets.module.css";

export interface SendSlackMessageTargetCardProps {
  index: number;
}

/** Where to post, and what to say. The message may carry `{{ }}` variables. */
export function SendSlackMessageTargetCard({
  index,
}: SendSlackMessageTargetCardProps) {
  const { control } = useFormContext<TriggerFormValues>();
  const { currentOrganization } = useAuth();
  const catalog = useFormTemplateCatalog();
  const channelTitleId = React.useId();
  const messageTitleId = React.useId();
  const channelsQuery = useSlackOutboundChannels(currentOrganization?.org_id);

  const channels: SelectItem<string>[] = (channelsQuery.data ?? []).map(
    (channel) => ({
      value: channel.channel_id,
      label: `#${channel.channel_name}`,
    }),
  );
  // Nothing to pick while the list loads, when it fails, or once it arrives
  // empty: the field says which, and stays shut rather than opening onto
  // nothing.
  const noChannels = channelsQuery.isSuccess && channels.length === 0;
  const unavailable = !channelsQuery.isSuccess || noChannels;
  const placeholder = channelPlaceholder({
    pending: channelsQuery.isPending,
    failed: channelsQuery.isError,
    empty: noChannels,
  });
  // The field already says the list is unreachable, so the line under it says
  // what to do about it instead of saying so again.
  const description = channelsQuery.isError ? (
    <span data-testid="slack-channels-error">
      Slack is not connected on this deployment. {""}
      <Link
        to={Goto.route.Settings.Integrations.Slack}
        className={styles.hintLink}
      >
        Connect it here
      </Link>
      .
    </span>
  ) : (
    "Only channels on the org's outbound allowlist can be posted to."
  );

  return (
    <div className={styles.body} data-testid={`trigger-target-${index}-slack`}>
      <FormBlock
        titleId={channelTitleId}
        title="Channel"
        hint="The Slack channel to post to when this trigger fires."
      >
        <Controller
          control={control}
          name={`targets.${index}.channelId`}
          render={({ field, fieldState }) => (
            <div data-testid={`trigger-target-${index}-channel`}>
              {/* Written out as parts, not left to the default body, so the
                  trigger can carry `field.ref`. */}
              <Select<string>
                items={channels}
                value={typeof field.value === "string" ? field.value : null}
                onValueChange={field.onChange}
                description={description}
                error={fieldState.error?.message}
                placeholder={placeholder}
                disabled={unavailable}
                size="lg"
              >
                <Select.Trigger
                  ref={field.ref}
                  aria-labelledby={channelTitleId}
                />
                <Select.Content>
                  {channels.map((channel) => (
                    <Select.Item key={channel.value} value={channel.value}>
                      {channel.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
            </div>
          )}
        />
      </FormBlock>

      <FormBlock
        titleId={messageTitleId}
        title="Message"
        hint="What to say. Reference trigger variables with {{ }}."
      >
        <Controller
          control={control}
          name={`targets.${index}.text`}
          render={({ field, fieldState }) => (
            <Field.Root
              invalid={fieldState.invalid}
              data-testid={`trigger-target-${index}-message`}
            >
              <TemplateField
                value={typeof field.value === "string" ? field.value : ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                catalog={catalog}
                labelledBy={messageTitleId}
                rows={3}
                error={fieldState.invalid}
              />
              <Field.Error match>{fieldState.error?.message}</Field.Error>
            </Field.Root>
          )}
        />
      </FormBlock>
    </div>
  );
}

/** What the channel field says, for the state of the channel list. */
function channelPlaceholder(list: {
  pending: boolean;
  failed: boolean;
  empty: boolean;
}): string {
  if (list.pending) {
    return "Loading channels\u2026";
  }
  if (list.failed) {
    return "Couldn\u2019t load channels";
  }
  if (list.empty) {
    return "No channels available";
  }
  return "Choose a channel";
}
